/**
 * MarketIntelDashboardPage — /omega/market-intel
 *
 * Main dashboard for the Market Intelligence Brain (Phase 40 V-025).
 * Category selector + combined market brief from /api/v1/market-intel/summary.
 *
 * Founder dashboard: the brief is rendered as readable CARDS (competitors,
 * regulatory alerts, TAM/pricing stats) — never raw JSON. Empty sections show
 * "No <section> data yet."
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

const CATEGORIES = [
  'Financial',
  'HealthAndWellbeing',
  'TechnologyAndInfrastructure',
  'WorkAndProductivity',
];

// ── Response shapes (defensive — fields are optional at runtime) ──────────────

interface TamRow {
  tam_usd_cents?: number;
  sam_usd_cents?: number;
  som_usd_cents?: number;
  confidence?: string;
  methodology?: string;
}
interface CompetitorEntry {
  name?: string;
  has_pricing?: boolean;
  on_marketplace?: boolean;
  has_job_postings?: boolean;
  observed_prices?: Array<{ annualized_usd?: number }>;
}
interface CompetitorScan {
  competitors_json?: string;
  top_competitor?: string | null;
  scan_sources?: string;
}
interface RegulatoryRow {
  signal_id?: string;
  regulation_name?: string;
  summary?: string;
  jurisdiction?: string;
  severity?: string;
  urgency?: string;
}
interface PricingRow {
  median_price_usd_cents?: number;
  price_range_low_usd_cents?: number;
  price_range_high_usd_cents?: number;
  pricing_model?: string;
  confidence?: string;
  sample_count?: number;
}
interface MarketSummary {
  category?: string;
  tam_estimates?: TamRow[];
  competitor_scan?: CompetitorScan | null;
  regulatory_signals?: RegulatoryRow[];
  pricing_benchmark?: PricingRow | null;
}

// ── Formatters ────────────────────────────────────────────────────────────────

/** USD cents → compact human dollar string (e.g. $1.2M, $84K, $500). */
function usd(cents: number | undefined | null): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(0)}`;
}

function parseCompetitors(json: string | undefined): CompetitorEntry[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as CompetitorEntry[];
    // Some scans wrap the list as { competitors: [...] }.
    if (parsed && Array.isArray(parsed.competitors)) return parsed.competitors;
    return [];
  } catch {
    return [];
  }
}

/** Human pricing line for a competitor from its observed prices. */
function competitorPricing(c: CompetitorEntry): string {
  const annual = c.observed_prices?.find((p) => Number.isFinite(p.annualized_usd))?.annualized_usd;
  if (annual != null) return `~$${Math.round(annual).toLocaleString()}/yr observed`;
  if (c.has_pricing) return 'Pricing published';
  return 'No pricing observed';
}

/** Human positioning line from a competitor's signal flags. */
function competitorPositioning(c: CompetitorEntry): string {
  const tags: string[] = [];
  if (c.on_marketplace) tags.push('On a marketplace');
  if (c.has_job_postings) tags.push('Actively hiring');
  return tags.length ? tags.join(' · ') : 'No positioning signals yet';
}

const sevColor: Record<string, string> = {
  Critical: '#dc2626',
  High: '#ea580c',
  Medium: '#d97706',
  Low: '#65a30d',
};

// ── Card primitives ───────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--omega-border, #2a2a30)',
  borderRadius: 8,
  padding: '12px 14px',
  background: 'var(--omega-canvas, #15151a)',
};
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 12,
};
const sectionTitle: React.CSSProperties = {
  fontSize: '10pt',
  fontWeight: 700,
  margin: '20px 0 10px',
};
const emptyStyle: React.CSSProperties = { fontSize: '9pt', opacity: 0.6 };

function StatCard({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: '8pt', textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.6 }}>
        {label}
      </div>
      <div style={{ fontSize: '15pt', fontWeight: 700, margin: '4px 0 2px' }}>{value}</div>
      {caption && <div style={{ fontSize: '8pt', opacity: 0.6 }}>{caption}</div>}
    </div>
  );
}

export function MarketIntelDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [category, setCategory] = useState('Financial');

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-intel-summary', category],
    queryFn: () => apiFetch<MarketSummary>(`/market-intel/summary?category=${category}`),
    enabled: !!category,
  });

  const tam = data?.tam_estimates?.[0];
  const competitors = parseCompetitors(data?.competitor_scan?.competitors_json ?? undefined);
  const regulatory = data?.regulatory_signals ?? [];
  const pricing = data?.pricing_benchmark ?? null;

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Market Intel' }]} />

      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 16px' }}>
        Market Intelligence
      </h1>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: '9pt', marginRight: 8 }}>Category:</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ fontSize: '9pt', padding: '4px 8px' }}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <p style={{ fontSize: '9pt' }}>Loading…</p>}
      {error && (
        <p style={{ fontSize: '9pt', color: '#f87171' }}>Could not load the market brief.</p>
      )}

      {data && !isLoading && (
        <>
          {/* Market size — TAM / SAM / SOM + pricing */}
          <h2 style={sectionTitle}>Market size</h2>
          {tam || pricing ? (
            <div style={gridStyle}>
              {tam && (
                <>
                  <StatCard label="TAM" value={usd(tam.tam_usd_cents)} caption="Total addressable" />
                  <StatCard label="SAM" value={usd(tam.sam_usd_cents)} caption="Serviceable" />
                  <StatCard label="SOM" value={usd(tam.som_usd_cents)} caption="Obtainable" />
                </>
              )}
              {pricing && (
                <StatCard
                  label="Median price"
                  value={usd(pricing.median_price_usd_cents)}
                  caption={
                    pricing.pricing_model
                      ? `${pricing.pricing_model}${
                          pricing.sample_count ? ` · ${pricing.sample_count} sampled` : ''
                        }`
                      : 'Benchmark'
                  }
                />
              )}
            </div>
          ) : (
            <p style={emptyStyle}>No market-size data yet.</p>
          )}

          {/* Competitors */}
          <h2 style={sectionTitle}>Competitors</h2>
          {competitors.length ? (
            <div style={gridStyle}>
              {competitors.map((c, i) => (
                <div key={c.name ?? i} style={cardStyle}>
                  <div style={{ fontSize: '10pt', fontWeight: 600 }}>{c.name ?? 'Unnamed'}</div>
                  <div style={{ fontSize: '8.5pt', margin: '4px 0 2px' }}>{competitorPricing(c)}</div>
                  <div style={{ fontSize: '8pt', opacity: 0.65 }}>{competitorPositioning(c)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={emptyStyle}>No competitor data yet.</p>
          )}

          {/* Regulatory alerts */}
          <h2 style={sectionTitle}>Regulatory alerts</h2>
          {regulatory.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {regulatory.map((r, i) => (
                <div key={r.signal_id ?? i} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    {r.severity && (
                      <span
                        style={{
                          fontSize: '7.5pt',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          color: sevColor[r.severity] ?? '#9ca3af',
                        }}
                      >
                        {r.severity}
                      </span>
                    )}
                    <span style={{ fontSize: '9.5pt', fontWeight: 600 }}>
                      {r.regulation_name ?? 'Regulatory signal'}
                    </span>
                    {r.jurisdiction && (
                      <span style={{ fontSize: '8pt', opacity: 0.55 }}>· {r.jurisdiction}</span>
                    )}
                  </div>
                  {r.summary && (
                    <div style={{ fontSize: '8.5pt', opacity: 0.8, marginTop: 3 }}>{r.summary}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={emptyStyle}>No regulatory data yet.</p>
          )}
        </>
      )}
    </div>
  );
}
